import { parseDateTime } from "@internationalized/date";
import type { ToolApprovalSibling } from "#components/ui/tool-approval/renderers/ToolApprovalRenderer.types.ts";
import { resolveCallIntegrationKey } from "#components/ui/tool-approval/toolApprovalDecision.ts";
import { MEMBER_ID } from "#features/conversations/utils/composerDocument.ts";
import { isObject, readString, readStringList, toHttpsUrl, type Fields } from "#features/conversations/lib/untypedFields.ts";

export type Translate = (key: string, options?: Record<string, unknown>) => string;

// Core's availability is shown for this long after it was checked, so a reload can't bring back a stale "free".
const AVAILABILITY_MAX_AGE_MS = 15 * 60_000;

// A wall-clock time as the call carries it, in the manager's Workleap timezone. Any trailing offset is ignored, as the tool does.
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

interface WallClock {
    date: string;
    // Midnight UTC of the date plus the time of day: a calendar to format, never an instant, so no timezone shifts it.
    utc: Date;
}

function parseWallClock(value: string): WallClock | null {
    const match = WALL_CLOCK.exec(value.trim());
    if (match === null) {
        return null;
    }
    const [year, month, day, hour, minute] = match.slice(1).map(Number) as [number, number, number, number, number];
    const utc = new Date(Date.UTC(year, month - 1, day, hour, minute));

    // Date.UTC rolls an impossible date over, so a value that doesn't read back as sent isn't a time.
    const isReal = utc.getUTCMonth() === month - 1 && utc.getUTCDate() === day && utc.getUTCHours() === hour && utc.getUTCMinutes() === minute;

    return isReal ? { date: `${match[1]}-${match[2]}-${match[3]}`, utc } : null;
}

// The date and time of day of a wall-clock value as a date input and a time input read them, "2026-10-21" and "10:00".
export function wallClockParts(value: string | null): { date: string; time: string } | null {
    const parsed = parseWallClock(value ?? "");

    return parsed === null ? null : { date: parsed.date, time: parsed.utc.toISOString().slice(11, 16) };
}

type WallClockParts = NonNullable<ReturnType<typeof wallClockParts>>;

const wallClockOf = ({ date, time }: WallClockParts) => parseDateTime(`${date}T${time}`);

// The event moved to start at `to`: its end keeps the same distance from the start, so a move never changes the event's length.
export function moveEvent(start: WallClockParts, end: WallClockParts, to: WallClockParts): { start: string; end: string } {
    const length = wallClockOf(end).toDate("UTC").getTime() - wallClockOf(start).toDate("UTC").getTime();

    return { start: wallClockOf(to).toString(), end: wallClockOf(to).add({ milliseconds: length }).toString() };
}

// Whether the end is at or before the start, which is no event.
export function endsNotAfterStart(start: WallClockParts, end: WallClockParts): boolean {
    return wallClockOf(end).compare(wallClockOf(start)) <= 0;
}

// Whether two calls are for the same start and end, whatever seconds or offset each carries.
export function haveSameTimes(a: Fields, b: Fields): boolean {
    return ["start", "end"].every(field => {
        const [first, second] = [wallClockParts(readString(a, field)), wallClockParts(readString(b, field))];

        return first?.date === second?.date && first?.time === second?.time;
    });
}

export function hasWallClockTimes(args: Fields): boolean {
    const start = readString(args, "start");
    const end = readString(args, "end");

    return start !== null && end !== null && parseWallClock(start) !== null && parseWallClock(end) !== null;
}

// "Wed, Oct 21 · 10:00 – 10:30 AM", or both dates in full when the event crosses midnight.
export function formatEventTime(args: Fields, locale: string): string | null {
    const start = parseWallClock(readString(args, "start") ?? "");
    const end = parseWallClock(readString(args, "end") ?? "");
    if (start === null || end === null) {
        return null;
    }
    const options = { timeZone: "UTC" } as const;
    if (end.utc < start.utc) {
        // formatRange throws on a reversed range, so each end reads on its own.
        const full = new Intl.DateTimeFormat(locale, { ...options, weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

        return `${full.format(start.utc)} – ${full.format(end.utc)}`;
    }
    const time = new Intl.DateTimeFormat(locale, { ...options, hour: "numeric", minute: "2-digit" });
    if (start.date === end.date) {
        const day = new Intl.DateTimeFormat(locale, { ...options, weekday: "short", month: "short", day: "numeric" });

        return `${day.format(start.utc)} · ${time.formatRange(start.utc, end.utc)}`;
    }

    return new Intl.DateTimeFormat(locale, { ...options, weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).formatRange(start.utc, end.utc);
}

export interface InviteOverlap {
    position: number;
    title: string;
}

// The first invite in the batch, in order, whose time overlaps this one. Half-open, so back-to-back invites don't overlap.
// The times compare as calendar values, so a different date never overlaps.
export function overlappingInvite(args: Fields, siblings: ToolApprovalSibling[]): InviteOverlap | null {
    const start = parseWallClock(readString(args, "start") ?? "");
    const end = parseWallClock(readString(args, "end") ?? "");
    if (start === null || end === null) {
        return null;
    }
    for (const { position, args: siblingArgs, edits } of siblings) {
        const shown = { ...siblingArgs, ...edits };
        const siblingStart = parseWallClock(readString(shown, "start") ?? "");
        const siblingEnd = parseWallClock(readString(shown, "end") ?? "");
        const title = readString(shown, "subject");
        if (title !== null && siblingStart !== null && siblingEnd !== null && start.utc < siblingEnd.utc && siblingStart.utc < end.utc) {
            return { position, title };
        }
    }

    return null;
}

export type CalendarProvider = "outlook" | "google";

export function providerOf(args: Fields): CalendarProvider | null {
    const key = resolveCallIntegrationKey(args);

    return key === "outlook" || key === "google" ? key : null;
}

export interface Attendee {
    label: string;
    photoUrl: string | null;
    isWarning: boolean;
}

interface DisplayAttendee {
    reference: string;
    status: string | null;
    memberId: string | null;
    name: string | null;
    photoUrl: string | null;
    totalMatches: number | null;
}

// Core sends one entry per proposed attendee, in order. An entry without its reference drops out.
function displayAttendeesOf(display: Fields): DisplayAttendee[] | null {
    if (!Array.isArray(display.attendees)) {
        return null;
    }

    return display.attendees.flatMap(entry => {
        const reference = isObject(entry) ? readString(entry, "reference") : null;
        if (!isObject(entry) || reference === null) {
            return [];
        }

        return [{
            reference,
            status: readString(entry, "status"),
            memberId: readString(entry, "member_id"),
            name: readString(entry, "name"),
            photoUrl: toHttpsUrl(readString(entry, "photo_url")),
            totalMatches: typeof entry.total_matches === "number" ? entry.total_matches : null
        }];
    });
}

// A member id means nothing to the reader.
function nameOf(reference: string, tShared: Translate): string {
    return MEMBER_ID.test(reference) ? tShared("calendarEvent.colleague") : reference;
}

export function attendeesOf(args: Fields, display: Fields, tShared: Translate): Attendee[] {
    const entries = displayAttendeesOf(display);
    if (entries === null) {
        return readStringList(args, "attendees").map(reference => ({ label: nameOf(reference, tShared), photoUrl: null, isWarning: false }));
    }

    return entries.map(({ reference, status, name, photoUrl, totalMatches }) => {
        if (status === "ambiguous") {
            return { label: tShared("calendarEvent.ambiguousAttendee", { count: totalMatches, name: reference }), photoUrl: null, isWarning: true };
        }
        if (status === "unknown") {
            return { label: tShared("calendarEvent.unknownAttendee", { name: nameOf(reference, tShared) }), photoUrl: null, isWarning: true };
        }

        return { label: name ?? nameOf(reference, tShared), photoUrl, isWarning: false };
    });
}

const firstNameOf = (name: string) => name.split(/\s+/)[0] ?? name;

// When core's check stops showing: null without a check, or one with no readable time.
export function availabilityExpiry(display: Fields): number | null {
    const checkedAt = isObject(display.availability) ? Date.parse(readString(display.availability, "checked_at") ?? "") : Number.NaN;

    return Number.isNaN(checkedAt) ? null : checkedAt + AVAILABILITY_MAX_AGE_MS;
}

export interface AvailabilityLine {
    text: string;
    // Everyone is free and nothing in the batch overlaps, so the line reassures rather than warns.
    isClear: boolean;
}

// The availability sentence, or null when there is none to show: core sent none, or its check is too old. An overlap with
// another invite in the batch joins it, or stands alone when there is no check to show, because core can't see unbooked invites.
export function availabilityLine(display: Fields, tShared: Translate, locale: string, now: number, overlap: InviteOverlap | null = null): AvailabilityLine | null {
    const overlapLine = overlap === null ? null : tShared("calendarEvent.overlap", { position: overlap.position, title: overlap.title });
    const warning = (text: string | null): AvailabilityLine | null => (text ? { text, isClear: false } : null);
    const withOverlap = (line: string) => warning([line, overlapLine].filter(Boolean).join(" "));
    const availability = display.availability;
    const entries = displayAttendeesOf(display);
    const expiresAt = availabilityExpiry(display);
    if (!isObject(availability) || entries === null || expiresAt === null || now >= expiresAt) {
        return warning(overlapLine);
    }

    const connection = readString(availability, "calendar_connection");
    if (connection === "notConnected" || connection === "expired") {
        return withOverlap(tShared("calendarEvent.availability.connectCalendar"));
    }
    const unmatched = entries.find(({ status }) => status === "ambiguous" || status === "unknown");
    if (unmatched !== undefined) {
        return withOverlap(tShared("calendarEvent.availability.nameThem", { name: nameOf(unmatched.reference, tShared) }));
    }

    const statusById = new Map<string, { status: string | null; isCurrentUser: boolean }>();
    for (const person of Array.isArray(availability.people) ? availability.people : []) {
        const memberId = isObject(person) ? readString(person, "member_id") : null;
        if (isObject(person) && memberId !== null) {
            statusById.set(memberId, { status: readString(person, "status"), isCurrentUser: person.is_current_user === true });
        }
    }
    const people = entries.map(({ memberId, name, reference }) => ({
        name: firstNameOf(name ?? nameOf(reference, tShared)),
        status: (memberId === null ? null : statusById.get(memberId)?.status) ?? "unknown"
    }));
    const you = [...statusById.values()].find(({ isCurrentUser }) => isCurrentUser)?.status ?? "unknown";
    const namesOf = (status: string) => people.filter(person => person.status === status).map(({ name }) => name);
    const list = (names: string[]) => new Intl.ListFormat(locale, { type: "conjunction" }).format(names);
    const [busy, unknown, free] = [namesOf("busy"), namesOf("unknown"), namesOf("free")];

    if (busy.length === 0 && unknown.length === 0 && you === "free") {
        if (overlap !== null) {
            return warning(free.length > 0
                ? tShared("calendarEvent.availability.allFreeButOverlaps", { count: free.length, names: list(free), position: overlap.position, title: overlap.title })
                : overlapLine);
        }

        return { text: free.length > 0 ? tShared("calendarEvent.availability.allFree", { count: free.length, names: list(free) }) : tShared("calendarEvent.availability.youFree"), isClear: true };
    }

    const sentences = [
        busy.length > 0 && tShared("calendarEvent.availability.busy", { count: busy.length, names: list(busy) }),
        you === "busy" && tShared("calendarEvent.availability.youBusy"),
        unknown.length > 0 && tShared("calendarEvent.availability.unreadable", { count: unknown.length, names: list(unknown) }),
        you === "unknown" && tShared("calendarEvent.availability.youUnreadable"),
        you === "free" && overlap === null && (free.length > 0 ? tShared("calendarEvent.availability.youAndFree", { names: list(free) }) : tShared("calendarEvent.availability.youFree")),
        (you !== "free" || overlap !== null) && free.length > 0 && tShared("calendarEvent.availability.free", { count: free.length, names: list(free) }),
        overlapLine
    ];

    return warning(sentences.filter(Boolean).join(" "));
}

const RECURRENCE_KEYS = new Map([
    ["daily", "calendarEvent.recurrenceDaily"],
    ["weekly", "calendarEvent.recurrenceWeekly"],
    ["monthly", "calendarEvent.recurrenceMonthly"],
    ["yearly", "calendarEvent.recurrenceYearly"]
]);

// Only how often it repeats; the manager changes the pattern through the chat.
export function recurrenceLabel(args: Fields, tShared: Translate): string | null {
    const recurrence = args.recurrence;
    if (!isObject(recurrence)) {
        return null;
    }
    const key = RECURRENCE_KEYS.get(readString(recurrence, "frequency") ?? "");
    const interval = typeof recurrence.interval === "number" ? recurrence.interval : 1;

    return key === undefined ? tShared("calendarEvent.repeats") : tShared(key, { count: interval });
}
